// Resumen de preparación de cargas de un pedido.
//
// Va encima de la subtabla de líneas y no como bandas dentro de ella: esa tabla
// ya está agrupada por factura, y carga y factura son agrupaciones ortogonales
// (una carga puede cruzar varias facturas y al revés).
//
// Cada carga se despliega para ver sus artículos. Un mismo pedido se reparte en
// cargas destinadas a clientes finales distintos, y eso lo dice la referencia de
// cliente de la cabecera.
//
// Aquí viven también las cargas que aún no existen: las solicitadas, que está
// tecleando un robot. Se pintan arriba, con borde discontinuo y sin barra de
// picking —no hay nada preparado todavía—, porque si no el usuario ve bajar el
// disponible de una línea sin saber por qué.
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { PickingBar } from '@/components/common/PickingBar'
import {
  BotonMarca, CabeceraMarca, FilaMarcable, MarcaCelda, useMarcada,
} from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type {
  CargaCreateResult, CargaEstadoPicking, CargaLinea, FiltroEstadoCarga,
  MarcasFilas, PedidoVentaLinea, SolicitudCarga,
} from '@/types'
import {
  agruparPorCarga, faltaPorReservar, filtrarPorEstado, totalPicking, totalPesos,
  type ResumenCarga, type ResumenPesos,
} from '@/utils/cargasPicking'
import type { CoincideArticulo } from '@/utils/filtroArticulo'
import { CargaDocumentosDrawer, EMPRESAS_CON_PACKING } from './CargaDocumentosDrawer'
import { NuevaCargaDrawer } from './NuevaCargaDrawer'
import { SIN_MARCAS } from './marcasBloque'
import { fmtQty, fmtPeso, fmtCajas, filaResaltada } from './formato'

const ESTADO_CLS: Record<CargaEstadoPicking, { dark: string; light: string }> = {
  pendiente:  { dark: 'bg-white/8 text-slate-400',          light: 'bg-slate-100 text-slate-500' },
  preparando: { dark: 'bg-amber-500/15 text-amber-300',     light: 'bg-amber-100 text-amber-700' },
  preparada:  { dark: 'bg-emerald-500/15 text-emerald-400', light: 'bg-emerald-100 text-emerald-700' },
}

export function EstadoBadge({ estado, dark }: { estado: CargaEstadoPicking; dark: boolean }) {
  const { t } = useTranslation()
  const cls = ESTADO_CLS[estado]
  return (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
      dark ? cls.dark : cls.light
    }`}>
      {t(`salesOrders.picking.estado.${estado}`)}
    </span>
  )
}

/** ¿Va paletizada la carga? Mismo formato que el estado.
 *
 *  Índigo para paletizada y contorno sin relleno para lo contrario: los tres
 *  colores rellenos (gris, ámbar, verde) ya son del estado, y un cuarto relleno
 *  se leería como un estado más.
 *
 *  El booleano llega ya resuelto del backend, que es donde vive la inversión del
 *  campo de origen (`_paletizada` en el router): `cargapaletizada_custom` = 1
 *  significa NO paletizada. */
export function PaletizadaBadge({ paletizada, dark }: { paletizada: boolean; dark: boolean }) {
  const { t } = useTranslation()
  const cls = paletizada
    ? (dark ? 'bg-indigo-500/15 text-indigo-300' : 'bg-indigo-100 text-indigo-700')
    : (dark ? 'border border-white/15 text-slate-400' : 'border border-slate-300 text-slate-500')
  return (
    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${cls}`}>
      {t(paletizada ? 'salesOrders.picking.paletizada' : 'salesOrders.picking.noPaletizada')}
    </span>
  )
}

/** ¿La carga sigue en el almacén o ya ha salido?
 *
 *  Se pinta siempre, porque es lo que se ha venido a mirar, pero con dos pesos
 *  muy distintos: expedida va rellena y en almacén sólo lleva texto apagado. El
 *  estado normal de una carga es estar en el almacén, y anunciarlo tan alto como
 *  la excepción llenaría la fila de ruido.
 *
 *  Su campo de origen (`estado_carga`) NO se lee invertido, a diferencia del de
 *  al lado: el backend lo resuelve en `_expedida`. */
export function EstadoAlmacenBadge({ expedida, dark }: { expedida: boolean; dark: boolean }) {
  const { t } = useTranslation()
  if (!expedida) {
    return (
      <span className={`text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
        dark ? 'text-slate-500' : 'text-slate-400'
      }`}>
        {t('salesOrders.picking.enAlmacen')}
      </span>
    )
  }
  return (
    <span className={`flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
      dark ? 'bg-cyan-500/15 text-cyan-300' : 'bg-cyan-100 text-cyan-700'
    }`}>
      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h13M13 6l6 6-6 6" />
      </svg>
      {t('salesOrders.picking.expedida')}
    </span>
  )
}

/** ¿La carga va por avión?
 *
 *  Sólo se pinta cuando lo es. De 55.685 cargas medidas, 219 son aéreas: marcar
 *  también las marítimas sería repetir lo normal en cada fila para señalar el
 *  0,4 % que no lo es.
 *
 *  Importa más de lo que su tamaño sugiere: el packing list de una carga aérea
 *  lleva tres niveles (air-palet › air-box › referencia) y exige que el almacén
 *  ya haya montado los air-boxes. Verlo aquí evita abrir el cajón para saberlo.
 *
 *  Su campo de origen (`es_aereo`) se lee tal cual, sin la inversión de
 *  `cargapaletizada_custom`: el backend lo resuelve en `_aerea`. */
export function AereaBadge({ aerea, dark }: { aerea: boolean; dark: boolean }) {
  const { t } = useTranslation()
  if (!aerea) return null
  return (
    <span
      title={t('salesOrders.picking.aereaTooltip')}
      className={`flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
        dark ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-100 text-violet-700'
      }`}
    >
      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.2 3.3a1.7 1.7 0 0 1 3.6 0L14 10l7 4v2l-7-2-.4 4.4 2.4 1.8V22l-4-1-4 1v-1.8l2.4-1.8L10 14l-7 2v-2l7-4z" />
      </svg>
      {t('salesOrders.picking.aerea')}
    </span>
  )
}

/** Estado de una carga en la pantalla dedicada, donde se ven muchas seguidas.
 *
 *  Sustituye a tres etiquetas de colores por una palabra y un punto. El criterio
 *  es que la información es lo que SE DESVÍA: de 18 cargas de un pedido, 16
 *  están preparadas, expedidas y paletizadas, así que anunciarlo con tres
 *  píldoras saturadas en cada fila no dice nada y tapa las dos que sí importan.
 *
 *  Sólo lleva color lo excepcional: seguir en el almacén. Lo terminado va en
 *  gris, que es como debe verse lo que ya no requiere ninguna acción.
 *
 *  Lo demás no se pinta aquí: el avance del picking ya lo dice la barra de la
 *  derecha, y «paletizada» sólo aparece cuando NO lo está. */
/** Por qué este peso no es de fiar, o cadena vacía si lo es.
 *
 *  Las tres razones se acumulan en un solo tooltip porque el usuario no necesita
 *  distinguirlas para actuar —en los tres casos hay que ir al maestro de
 *  artículos—, pero sí necesita saber cuál le ha tocado antes de llamar a nadie. */
export function avisoPeso(p: ResumenPesos, t: TFunction): string {
  const motivos: string[] = []
  if (p.neto == null && p.bruto == null) motivos.push(t('salesOrders.picking.pesoSinDato'))
  else if (p.sinPeso > 0) motivos.push(t('salesOrders.picking.pesoIncompleto', { count: p.sinPeso }))
  if (p.sospechoso) motivos.push(t('salesOrders.picking.pesoSospechoso'))
  return motivos.join(' ')
}

/** Bultos y peso de una carga (o del pedido entero).
 *
 *  El rótulo del bruto dice «(sin palet)» en vez de esconderlo en el tooltip: es
 *  la cifra que alguien copia a un transporte, y una advertencia que sólo se ve
 *  al pasar el ratón no llega ni al pantallazo ni al papel.
 *
 *  Cuando falta peso se pinta igual lo que hay —esconderlo dejaría 881 cargas en
 *  blanco sin explicación— pero con el aviso al lado. Lo que NO se hace nunca es
 *  rellenar el hueco con un cero. */
/** Reservada frente a solicitada, en la cabecera de la carga.
 *
 *  Va aparte del par «preparada / solicitada» y de la barra, que están a un palmo,
 *  porque contesta otra pregunta: la barra dice cómo va la preparación y esto dice
 *  si hay stock atado para terminarla. Meter una tercera cifra en el par habría
 *  dejado tres números sin rótulo a 10 px.
 *
 *  Se pinta SIEMPRE, también cuando cuadra: es un campo que alguien va a buscar, y
 *  un campo que desaparece cuando todo va bien se lee como un campo que falta. Lo
 *  que aparece y desaparece es el ámbar, que sólo enciende con hueco. */
export function ReservaCarga({
  solicitada, reservada, dark, carga,
}: { solicitada: number; reservada: number; dark: boolean; carga?: string }) {
  const { t } = useTranslation()
  const falta = faltaPorReservar(solicitada, reservada)
  const hueco = falta > 0

  return (
    <span
      title={t(hueco ? 'salesOrders.picking.reservaTooltipHueco' : 'salesOrders.picking.reservaTooltip', {
        carga: carga ?? '',
        reservada: fmtQty(reservada),
        solicitada: fmtQty(solicitada),
        falta: fmtQty(falta),
      })}
      className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${
        hueco
          ? (dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700')
          : (dark ? 'text-slate-400' : 'text-slate-500')
      }`}
    >
      {t('salesOrders.picking.reserved')}
      <span className={`font-semibold tabular-nums ${
        hueco ? '' : (dark ? 'text-slate-200' : 'text-slate-700')
      }`}>
        {fmtQty(reservada)} / {fmtQty(solicitada)}
      </span>
    </span>
  )
}

export function PesosCarga({
  pesos, dark, compacto = false,
}: { pesos: ResumenPesos; dark: boolean; compacto?: boolean }) {
  const { t } = useTranslation()
  const aviso = avisoPeso(pesos, t)
  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const valTxt = dark ? 'text-slate-200' : 'text-slate-700'
  const kg = t('salesOrders.picking.kg')

  // Ni peso ni cajas: la carga no tiene nada que contar y una ristra de rayas
  // sólo añadiría ruido. El aviso sí se queda: es la información que hay.
  const vacia = pesos.cajas == null && pesos.neto == null && pesos.bruto == null

  return (
    <span className={`flex items-center gap-2 ${compacto ? 'text-[10px]' : 'text-[11px]'} ${mutedTxt}`}>
      {!vacia && (
        <span
          className="flex items-center gap-2 whitespace-nowrap"
          title={t('salesOrders.picking.pesoTooltip')}
        >
          {pesos.cajas != null && (
            <span>
              <span className={`font-semibold tabular-nums ${valTxt}`}>{fmtCajas(pesos.cajas)}</span>
              {' '}{t('salesOrders.picking.cajas').toLowerCase()}
            </span>
          )}
          <span>
            <span className={`font-semibold tabular-nums ${valTxt}`}>{fmtPeso(pesos.neto)}</span>
            {' '}{kg} {t('salesOrders.picking.neto').toLowerCase()}
          </span>
          <span>
            <span className={`font-semibold tabular-nums ${valTxt}`}>{fmtPeso(pesos.bruto)}</span>
            {' '}{kg} {t('salesOrders.picking.bruto').toLowerCase()}
          </span>
        </span>
      )}
      {aviso && (
        <span
          title={aviso}
          className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${
            dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <svg className="w-2.5 h-2.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M8 1.8L15 14H1L8 1.8z" strokeLinejoin="round" />
            <path d="M8 6v3.5M8 11.6v.1" />
          </svg>
          {t('salesOrders.picking.pesoRevisar')}
        </span>
      )}
    </span>
  )
}

function EstadoCargaSobrio({ expedida, dark }: { expedida: boolean; dark: boolean }) {
  const { t } = useTranslation()
  // Sólo uno de los dos estados pide algo al usuario. Una carga expedida ya no
  // se toca y va en gris; la que sigue en almacén es la que hay que ver desde el
  // otro lado de la mesa, así que lleva fondo y peso. Con los dos en texto llano
  // —como estaba— había que leer la palabra para distinguirlos.
  if (expedida) {
    return (
      <span className={`flex items-center gap-1.5 text-[11px] font-medium whitespace-nowrap ${
        dark ? 'text-slate-400' : 'text-slate-500'
      }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${dark ? 'bg-slate-600' : 'bg-slate-300'}`} />
        {t('salesOrders.picking.expedida')}
      </span>
    )
  }
  return (
    <span className={`flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[11px] font-bold whitespace-nowrap ${
      dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-50 text-amber-700'
    }`}>
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      {t('salesOrders.picking.enAlmacen')}
    </span>
  )
}

interface Props {
  dark: boolean
  cargas: CargaLinea[]
  /** transid → nº de línea del pedido, para etiquetar el detalle. */
  lineaPorTransid?: Map<string, number | undefined>
  /** transid → descripción del artículo. */
  descripcionPorTransid?: Map<string, string | undefined>
  /** Búsqueda de artículo activa: las cargas que lo contienen arrancan
   *  desplegadas y sus filas coincidentes van resaltadas. */
  coincide?: CoincideArticulo | null
  /** Empresa y pedido de la cabecera. Vienen de fuera y no de `cargas[0]` porque
   *  hacen falta justo cuando todavía no hay ninguna carga. */
  empresa?: string
  numPedido?: string
  /** Líneas del pedido: lo que se puede meter en una carga nueva. */
  lineas?: PedidoVentaLinea[]
  /** Cargas pedidas que aún no existen. Reservan cantidad. */
  solicitudes?: SolicitudCarga[]
  /** Estado de carga elegido en el filtro de arriba: decide qué cargas se
   *  pintan aquí. No afecta a nada más de la pantalla. */
  filtroEstado?: FiltroEstadoCarga
  /** ¿El pedido admite cargas nuevas? Uno cerrado, no. */
  puedeCrear?: boolean
  onSolicitada?: (resultado: CargaCreateResult) => void
  /** Dónde se está pintando el bloque.
   *
   *  `lista` (por defecto) es la subtabla del listado: marco propio, cabecera
   *  propia y una etiqueta por cada atributo, porque ahí se ve una carga suelta
   *  dentro de un pedido concreto.
   *
   *  `pagina` es la pantalla dedicada, donde se ven las 18 cargas de un pedido
   *  seguidas. Ahí repetir «PREPARADA · EXPEDIDA · PALETIZADA» en dieciséis
   *  filas idénticas no informa de nada: deja de ser señal y pasa a ser textura.
   *  Así que sólo se pinta lo que se sale de lo normal, y el resto va en texto
   *  llano. Ver `EstadoCargaSobrio`. */
  variante?: 'lista' | 'pagina'
  /** Cajón de creación gobernado desde fuera, para que el botón «Nueva carga»
   *  pueda vivir en la cabecera de sección de la pantalla dedicada. Si no se
   *  pasan, manda el estado interno y todo sigue igual. */
  abrirNueva?: boolean
  onAbrirNueva?: (abierta: boolean) => void
  /** Orden de expansión que llega de fuera.
   *
   *  `modo` decide qué hacen las cargas que el usuario NO ha tocado: `auto` las
   *  abre si casan con la búsqueda, `todas` / `ninguna` las fuerza. El `nonce`
   *  es lo que reabre la mano: al cambiarlo se olvida lo que el usuario había
   *  abierto o cerrado a mano, que es justo lo que hay que hacer cuando cambia
   *  el filtro —si no, la carga que acaba de aparecer seguiría cerrada porque
   *  el usuario la cerró hace cinco minutos con otro filtro puesto—. */
  expansion?: { modo: 'auto' | 'todas' | 'ninguna'; nonce: number }
  /** Marcas del usuario.
   *
   *  Las pasan las DOS pantallas que pintan este bloque —la lista
   *  (`variante="lista"`) y la pantalla dedicada (`variante="pagina"`)—, cada una
   *  con su propio almacén: en el detalle el ámbito es el del pedido que se está
   *  mirando, no el de la lista, así que los contadores de cada pantalla hablan
   *  sólo de lo que allí se ve.
   *
   *  Sigue siendo opcional para que el bloque pueda montarse sin marcas —en un
   *  test o en una pantalla futura— sin arrastrar un almacén: sin ella no hay
   *  canalón y todo se pinta como antes, gracias al almacén inerte de
   *  `marcasBloque.ts`. */
  marcas?: MarcasFilas
  /** Clave de la fila del pedido; la de cada carga arranca por ella. */
  clavePedido?: string
}

export function CargasPedido({
  dark, cargas, lineaPorTransid, descripcionPorTransid, coincide, filtroEstado = '',
  empresa, numPedido, lineas = [], solicitudes = [], puedeCrear = false, onSolicitada,
  variante = 'lista', abrirNueva, onAbrirNueva, expansion, marcas, clavePedido,
}: Props) {
  const { t } = useTranslation()
  // Sólo lo que el usuario ha tocado; el resto sigue al despliegue automático.
  const [manual, setManual] = useState<Map<string, boolean>>(new Map())
  // Un cajón para toda la tarjeta y no uno por fila: sólo puede haber uno abierto.
  const esPagina = variante === 'pagina'
  const [documentos, setDocumentos] = useState<ResumenCarga | null>(null)
  // Controlado si el padre pasa `abrirNueva`; si no, con estado propio.
  const [creandoInterno, setCreandoInterno] = useState(false)
  const creando = abrirNueva ?? creandoInterno
  const setCreando = (v: boolean) => {
    if (onAbrirNueva) onAbrirNueva(v)
    else setCreandoInterno(v)
  }

  // Las filas de carga sólo traen el código; la descripción sale del mapa, para
  // que el criterio sea el mismo que en las facturas.
  const autoAbrir = useMemo(() => {
    const s = new Set<string>()
    if (!coincide) return s
    for (const c of cargas) {
      if (coincide(c.articulo, descripcionPorTransid?.get(c.transid))) s.add(c.carga)
    }
    return s
  }, [cargas, coincide, descripcionPorTransid])

  // Al cambiar la orden de fuera se descartan los toques manuales: vuelve a
  // mandar el criterio nuevo, y a partir de ahí el usuario puede colapsar lo
  // que quiera.
  useEffect(() => { setManual(new Map()) }, [expansion?.nonce])

  const abierta = (carga: string) => {
    const tocada = manual.get(carga)
    if (tocada !== undefined) return tocada
    if (expansion?.modo === 'todas') return true
    if (expansion?.modo === 'ninguna') return false
    return autoAbrir.has(carga)
  }

  function toggle(carga: string) {
    const siguiente = !abierta(carga)
    setManual(prev => new Map(prev).set(carga, siguiente))
  }

  // El filtro de arriba sólo afecta a ESTE bloque. Las cargas que no cuadran no
  // se pintan y sus cifras no entran en el avance de la cabecera, que si no
  // contradiría a las filas que hay debajo.
  //
  // Lo que NO se toca es la lista completa: la columna de picking de las líneas
  // y el cálculo del disponible del cajón siguen leyendo todas las cargas.
  // Filtrarlas ahí subiría el disponible y dejaría pedir de más.
  const visibles = filtrarPorEstado(cargas, filtroEstado)
  // Una solicitud no ha salido del almacén: todavía no existe ninguna carga.
  const solicitudesVisibles = filtroEstado === 'expedida' ? [] : solicitudes

  const porCarga = agruparPorCarga(visibles)
  const total = totalPicking(visibles)
  // Bultos y peso de TODAS las cargas visibles del pedido. Se suma sobre las
  // mismas filas que la barra de arriba, así que el filtro de estado mueve las
  // dos cifras a la vez y no queda un peso que no case con lo que se ve.
  const pesosPedido = totalPesos(visibles)

  // El bloque sólo desaparece si no hay nada que enseñar NI nada que hacer: su
  // cabecera es la única puerta de entrada a crear una carga, y esconderla en un
  // pedido sin cargas dejaría la función inalcanzable justo cuando hace falta.
  // Sin tarjeta manda la sección de fuera, que ya ha pintado su título: aquí
  // desaparecer dejaría esa cabecera colgando sin explicación.
  const puedeAbrir = puedeCrear && !!empresa && !!numPedido && lineas.length > 0
  if (variante === 'lista' && porCarga.length === 0 && solicitudesVisibles.length === 0 && !puedeAbrir) {
    return null
  }

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const divider  = dark ? 'border-white/8' : 'border-slate-200'
  const cardBg   = dark ? 'bg-white/[0.03] border-white/8' : 'bg-slate-50/80 border-slate-200'

  // Clave de negocio de una carga (o de una solicitud): el pedido más su número.
  // Nunca el índice — el filtro de estado de arriba quita filas de en medio.
  const marcable = !!marcas && !!clavePedido
  const claveDe = (tipo: 'carga' | 'solicitud', id: string) =>
    marcable ? claveFila(clavePedido, tipo, id) : ''

  return (
    <div className={esPagina ? '' : `mb-3 rounded-lg border overflow-hidden ${cardBg}`}>
      {/* Cabecera: título + avance global */}
      {!esPagina && (
      <div className={`flex items-center gap-3 flex-wrap px-3 py-2 border-b ${divider}`}>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>
          {t('salesOrders.picking.title')}
        </span>
        {porCarga.length > 0 && (
          <span className={`text-[10px] ${mutedTxt}`}>
            {t('salesOrders.picking.cargasCount', { count: porCarga.length })}
          </span>
        )}
        {solicitudesVisibles.length > 0 && (
          <span className={`text-[10px] font-semibold ${dark ? 'text-amber-400' : 'text-amber-600'}`}>
            + {t('salesOrders.nuevaCarga.solicitadasCount', { count: solicitudesVisibles.length })}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2.5">
          {puedeAbrir && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors cursor-pointer ${
                dark ? 'text-sky-300 bg-sky-500/10 hover:bg-sky-500/20'
                     : 'text-sky-700 bg-sky-50 hover:bg-sky-100'
              }`}
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {t('salesOrders.nuevaCarga.boton')}
            </button>
          )}
          {porCarga.length > 0 && (
            <>
              {/* Total del pedido: lo que pesa todo lo que va en sus cargas. */}
              <PesosCarga pesos={pesosPedido} dark={dark} compacto />
              <span className={`text-[10px] tabular-nums ${mutedTxt}`}>
                {fmtQty(total.picking)} / {fmtQty(total.solicitada)} {t('salesOrders.picking.units')}
              </span>
              <div className="w-[130px]">
                <PickingBar
                  pct={total.pct}
                  dark={dark}
                  title={t('salesOrders.picking.totalTooltip', {
                    picking: fmtQty(total.picking),
                    solicitada: fmtQty(total.solicitada),
                    pendiente: fmtQty(total.pendiente),
                  })}
                />
              </div>
            </>
          )}
        </div>
      </div>
      )}

      <ul>
        {solicitudesVisibles.map((s, i) => (
          <SolicitudFila
            key={s.id}
            solicitud={s}
            dark={dark}
            primera={i === 0}
            lineaPorTransid={lineaPorTransid}
            descripcionPorTransid={descripcionPorTransid}
            marcas={marcable ? marcas : undefined}
            clave={claveDe('solicitud', s.id)}
          />
        ))}
        {porCarga.map((c, i) => (
          <CargaFila
            key={c.carga}
            carga={c}
            dark={dark}
            primera={i === 0 && solicitudesVisibles.length === 0}
            abierta={abierta(c.carga)}
            onToggle={() => toggle(c.carga)}
            lineaPorTransid={lineaPorTransid}
            descripcionPorTransid={descripcionPorTransid}
            coincide={coincide}
            esPagina={esPagina}
            onDocumentos={() => setDocumentos(c)}
            marcas={marcable ? marcas : undefined}
            clave={claveDe('carga', c.carga)}
          />
        ))}
      </ul>

      {porCarga.length === 0 && solicitudesVisibles.length === 0 && (
        <p className={`px-3 py-2.5 text-[11px] ${mutedTxt}`}>
          {/* «No hay» y «el filtro las esconde» son cosas distintas: con el
              segundo texto el usuario sabe que le queda un filtro puesto. */}
          {t(filtroEstado && cargas.length > 0
            ? 'salesOrders.picking.sinCargasEnEstado'
            : 'salesOrders.nuevaCarga.sinCargas')}
        </p>
      )}

      {creando && empresa && numPedido && (
        <NuevaCargaDrawer
          dark={dark}
          empresa={empresa}
          numPedido={numPedido}
          lineas={lineas}
          cargas={cargas}
          solicitudes={solicitudes}
          onClose={() => setCreando(false)}
          onEnviada={(resultado) => onSolicitada?.(resultado)}
        />
      )}

      {documentos && (
        <CargaDocumentosDrawer
          dark={dark}
          empresa={documentos.empresa}
          numPedido={documentos.numPedido}
          carga={documentos.carga}
          estado={documentos.estado}
          paletizada={documentos.paletizada}
          aerea={documentos.aerea}
          onClose={() => setDocumentos(null)}
        />
      )}
    </div>
  )
}

// ── Una carga: cabecera pulsable + detalle desplegable ───────────────────────

function CargaFila({
  carga: c, dark, primera, abierta, onToggle, lineaPorTransid, descripcionPorTransid, coincide,
  esPagina = false, onDocumentos, marcas, clave,
}: {
  carga: ResumenCarga
  dark: boolean
  primera: boolean
  abierta: boolean
  onToggle: () => void
  lineaPorTransid?: Map<string, number | undefined>
  descripcionPorTransid?: Map<string, string | undefined>
  coincide?: CoincideArticulo | null
  /** Presentación sobria de la pantalla dedicada (ver `variante` en Props). */
  esPagina?: boolean
  onDocumentos: () => void
  /** Sin almacén de marcas no hay canalón: es la pantalla de detalle. */
  marcas?: MarcasFilas
  clave: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  // El hook se llama siempre —no puede ir dentro de un `if`—: sin marcas se le da
  // el almacén inerte, que nunca cambia y por tanto nunca repinta.
  const marcada = useMarcada(marcas ?? SIN_MARCAS, clave)
  const marcable = !!marcas
  // La misma clase que usa `FilaMarcable`: una utilidad de Tailwind la perdería
  // contra el `hover:bg-…` que ya lleva la fila.
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'

  // Por nº de línea del pedido, que es como se leen las líneas en el resto de la
  // pantalla. El nº vive en el mapa, no en la fila de carga, así que el orden se
  // resuelve aquí; sin mapa se queda el de artículo que trae agruparPorCarga.
  const lineasOrdenadas = useMemo(() => {
    if (!lineaPorTransid) return c.lineas
    return [...c.lineas].sort((a, b) => {
      const na = lineaPorTransid.get(a.transid)
      const nb = lineaPorTransid.get(b.transid)
      // Las que no casan con ninguna línea van al final, no arriba.
      if (na == null && nb == null) return a.articulo.localeCompare(b.articulo, 'es-ES')
      if (na == null) return 1
      if (nb == null) return -1
      return na - nb || a.articulo.localeCompare(b.articulo, 'es-ES')
    })
  }, [c.lineas, lineaPorTransid])

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const mainTxt  = dark ? 'text-slate-100' : 'text-slate-800'
  const divider  = dark ? 'border-white/8' : 'border-slate-200'
  const hover    = dark ? 'hover:bg-white/[0.04]' : 'hover:bg-white/70'
  const subTh    = `px-2 py-1 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`
  // Sin color: las celdas que pintan de otro color lo añaden EN LUGAR de éste.
  // Dos utilidades `text-…` en la misma celda no se resuelven por orden en la
  // clase sino por su orden en la hoja de estilos, así que acumularlas deja el
  // color a suerte — el ámbar del bruto incoherente se perdía justo así.
  const subTdBase = 'px-2 py-1 text-[11px] whitespace-nowrap'
  const txtNormal = dark ? 'text-slate-300' : 'text-slate-700'
  const txtAviso  = dark ? 'text-amber-300' : 'text-amber-700'
  const subTd    = `${subTdBase} ${txtNormal}`

  return (
    <li className={primera ? '' : `border-t ${divider}`}>
      <div className={`flex items-stretch gap-2 pr-2 transition-colors duration-150 ${
        marcada ? marcadoBg : hover
      }`}>
        {marcable && (
          // El botón vive fuera del botón de desplegar y no dentro: si no, el
          // clic de marcar burbujearía y abriría la carga a la vez.
          <span className="flex items-center pl-2">
            <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
          </span>
        )}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={abierta}
          className={`flex-1 min-w-0 flex items-center gap-3 flex-wrap py-1.5 text-left cursor-pointer ${
            marcable ? 'pl-1 pr-3' : 'px-3'
          }`}
        >
          <svg
            className={`w-3 h-3 shrink-0 transition-transform duration-150 ${abierta ? 'rotate-90' : ''} ${mutedTxt}`}
            viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M5 11l4-4-4-4" />
          </svg>
          <span className={esPagina
            ? `font-mono text-[12px] font-semibold ${dark ? 'text-slate-50' : 'text-slate-900'}`
            : `font-mono text-[11px] font-semibold ${mainTxt}`}>
            {c.carga}
          </span>

          {/* Destino de esta carga. En la pantalla dedicada va en texto llano:
              es un NOMBRE, no un estado, y pintarlo como píldora de color lo
              hacía competir con los estados de al lado. */}
          {c.referenciaCliente && (
            <span
              title={t('salesOrders.picking.referenciaTooltip')}
              className={esPagina
                ? `text-[11px] font-semibold whitespace-nowrap ${mainTxt}`
                : `text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                    dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-100 text-sky-700'
                  }`}
            >
              {c.referenciaCliente}
            </span>
          )}

          {/* Segunda referencia de la carga. En la lista va en verde azulado para
              no confundirla con la de arriba: son dos campos distintos del mismo
              origen. En la pantalla, monoespaciada y apagada. */}
          {c.referenciaCliente2 && (
            <span
              title={t('salesOrders.picking.referencia2Tooltip')}
              className={esPagina
                ? `font-mono text-[10px] whitespace-nowrap ${mutedTxt}`
                : `text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
                    dark ? 'bg-teal-500/15 text-teal-300' : 'bg-teal-100 text-teal-700'
                  }`}
            >
              {c.referenciaCliente2}
            </span>
          )}

          {esPagina ? (
            <>
              <EstadoCargaSobrio expedida={c.expedida} dark={dark} />
              {/* Sólo la excepción: lo paletizado es la norma y no se anuncia. */}
              {c.paletizada === false && (
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap ${
                  dark ? 'border-amber-500/25 text-amber-300' : 'border-amber-300 text-amber-700'
                }`}>
                  {t('salesOrders.picking.noPaletizada')}
                </span>
              )}
            </>
          ) : (
            <>
              <EstadoBadge estado={c.estado} dark={dark} />
              <EstadoAlmacenBadge expedida={c.expedida} dark={dark} />
              {c.paletizada != null && <PaletizadaBadge paletizada={c.paletizada} dark={dark} />}
            </>
          )}
          <AereaBadge aerea={c.aerea} dark={dark} />
          <span className={`text-[10px] ${mutedTxt}`}>
            {t('salesOrders.picking.articulosCount', { count: c.articulos })}
          </span>

          {/* Bultos y peso de la carga, sumados de sus líneas. Van con el resto
              de lo que describe la carga y no junto a la barra: la barra dice
              cómo va la preparación y esto dice qué se manda. */}
          <PesosCarga pesos={c.pesos} dark={dark} compacto />

          {/* Reservada vs solicitada. Va con lo que describe la carga, no con la
              barra: la barra mide el avance y esto mide si hay con qué avanzar. */}
          <ReservaCarga
            solicitada={c.solicitada}
            reservada={c.reservada}
            dark={dark}
            carga={c.carga}
          />

          <span className="ml-auto flex items-center gap-2.5">
            <span className={`text-[10px] tabular-nums ${mutedTxt}`}>
              {fmtQty(c.picking)} / {fmtQty(c.solicitada)}
            </span>
            {/* Una carga terminada no necesita barra: dieciséis barras verdes al
                100 % son ruido, y lo que se busca de un vistazo es la que NO lo
                está. Así la barra sólo aparece donde queda trabajo. */}
            {esPagina && c.pct >= 100 ? (
              <span
                className={`flex items-center gap-1 w-[130px] text-[11px] font-semibold tabular-nums ${mutedTxt}`}
                title={t('salesOrders.picking.cargaTooltip', {
                  carga: c.carga,
                  picking: fmtQty(c.picking),
                  solicitada: fmtQty(c.solicitada),
                  pendiente: fmtQty(c.pendiente),
                })}
              >
                <svg className="w-3 h-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8.5l3.5 3.5L13 5" />
                </svg>
                100%
              </span>
            ) : (
              <span className="w-[130px]">
                <PickingBar
                  pct={c.pct}
                  dark={dark}
                  title={t('salesOrders.picking.cargaTooltip', {
                    carga: c.carga,
                    picking: fmtQty(c.picking),
                    solicitada: fmtQty(c.solicitada),
                    pendiente: fmtQty(c.pendiente),
                  })}
                />
              </span>
            )}
          </span>
        </button>

        <DocumentosBoton
          dark={dark}
          empresa={c.empresa}
          carga={c.carga}
          onClick={onDocumentos}
        />
      </div>

      {abierta && (
        <div className={`px-3 pb-2 pl-8 overflow-x-auto border-t ${divider}`}>
          {/* Anchos declarados: cada carga pinta su propia tabla y con el ancho
              automático cada una salía distinta según la descripción. Los 720 px
              son el mínimo: con `w-full` la tabla se estira a lo que haya y
              `table-fixed` reparte el sobrante en proporción a estos anchos, así
              que todas las cargas siguen cuadrando entre sí. */}
          <table className={`w-full table-fixed border-collapse text-left ${
            marcable ? 'min-w-[1134px]' : 'min-w-[1110px]'
          }`}>
            <colgroup>
              {/* Canalón de marcas: una columna más de la rejilla, de 24 px. */}
              {marcable && <col className="w-[24px]" />}
              <col className="w-[44px]" />
              <col className="w-[260px]" />
              {/* Solicitada · Reservada · Preparada · Pendiente. La reservada entra
                  en segunda posición para que se lea al lado de lo que se pidió
                  preparar, que es contra lo que se compara. */}
              <col className="w-[100px]" />
              <col className="w-[100px]" />
              <col className="w-[100px]" />
              <col className="w-[100px]" />
              {/* Bultos y peso, entre las cantidades y el avance: las tres
                  columnas de unidades describen cuánto, éstas describen qué
                  bulto sale, y la barra cierra con cómo va. */}
              <col className="w-[70px]" />
              <col className="w-[100px]" />
              <col className="w-[120px]" />
              <col className="w-[116px]" />
            </colgroup>
            <thead>
              <tr>
                {marcable && <CabeceraMarca />}
                <th className={subTh}>{t('salesOrders.line.lineNum')}</th>
                <th className={subTh}>{t('salesOrders.line.article')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.requested')}</th>
                <th className={`${subTh} text-right`} title={t('salesOrders.picking.reservaColTooltip')}>
                  {t('salesOrders.picking.reserved')}
                </th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.picked')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.remaining')}</th>
                <th className={`${subTh} text-right`} title={t('salesOrders.picking.cajasTooltip')}>
                  {t('salesOrders.picking.cajas')}
                </th>
                <th className={`${subTh} text-right`} title={t('salesOrders.picking.pesoTooltip')}>
                  {t('salesOrders.picking.neto')} ({t('salesOrders.picking.kg')})
                </th>
                {/* El «sin palet» va en la cabecera y no sólo en el tooltip: es la
                    cifra que se copia a un transporte. Y va en su propia línea
                    porque en una sola —con el `nowrap` de `subTh`— se salía de la
                    columna y se montaba encima de la de picking. */}
                <th
                  className={`${subTh} text-right whitespace-normal leading-tight`}
                  title={t('salesOrders.picking.pesoTooltip')}
                >
                  {t('salesOrders.picking.brutoCorto')} ({t('salesOrders.picking.kg')})
                  <span className="block font-normal normal-case tracking-normal text-[8px]">
                    {t('salesOrders.picking.sinPalet')}
                  </span>
                </th>
                <th className={subTh}>{t('salesOrders.picking.col')}</th>
              </tr>
            </thead>
            <tbody>
              {lineasOrdenadas.map((l, i) => {
                const pct = l.qtySolicitada > 0 ? (l.qtyPicking / l.qtySolicitada) * 100 : 0
                const numLinea = lineaPorTransid?.get(l.transid)
                const descripcion = descripcionPorTransid?.get(l.transid)
                const resaltada = !!coincide?.(l.articulo, descripcion)
                // Clave de negocio de la fila: la carga, la transacción de
                // inventario y el artículo. El backend devuelve una fila por
                // (carga, artículo), así que esto la identifica sin recurrir al
                // índice —que se movería al reordenar por nº de línea—.
                const claveL = marcable ? claveFila(clave, l.transid, l.articulo) : ''
                return (
                  <FilaMarcable
                    key={`${l.transid}-${i}`}
                    marcas={marcas ?? SIN_MARCAS}
                    clave={claveL}
                    dark={dark}
                    className={`border-t ${divider} ${resaltada ? filaResaltada(dark) : ''}`}
                  >
                    {marcable && (
                      <MarcaCelda marcas={marcas} clave={claveL} dark={dark} densa />
                    )}
                    <td className={`${subTdBase} tabular-nums ${mutedTxt}`}>{numLinea ?? '—'}</td>
                    <td className={subTd}>
                      <div className="max-w-[220px]">
                        <div className="font-semibold text-[11px] truncate" title={fmtArt(l.articulo)}>
                          {fmtArt(l.articulo)}
                        </div>
                        {descripcion && (
                          <div className={`text-[9px] truncate ${mutedTxt}`} title={descripcion}>
                            {descripcion}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className={`${subTd} text-right tabular-nums`}>{fmtQty(l.qtySolicitada)}</td>
                    {/* En ámbar cuando se reservó menos de lo que se mandó preparar:
                        es la fila que explica el aviso de la cabecera de la carga.
                        Aquí el 0 es un cero de verdad —no hay reserva— y no un hueco,
                        así que se pinta como número y no como raya. */}
                    <td className={`${subTdBase} text-right tabular-nums ${
                      l.qtyReservada < l.qtySolicitada ? txtAviso : txtNormal
                    }`}>
                      {fmtQty(l.qtyReservada)}
                    </td>
                    <td className={`${subTdBase} text-right tabular-nums ${dark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {fmtQty(l.qtyPicking)}
                    </td>
                    <td className={`${subTd} text-right tabular-nums font-semibold`}>
                      {fmtQty(l.qtyPendiente)}
                    </td>
                    {/* La raya de `fmtPeso`/`fmtCajas` dice «el maestro no tiene
                        el peso de este artículo». No es un cero ni una celda en
                        blanco: es el hueco que suma el aviso de la cabecera. */}
                    <td className={`${subTdBase} text-right tabular-nums ${
                      l.cajas == null ? mutedTxt : txtNormal
                    }`}>
                      {fmtCajas(l.cajas)}
                    </td>
                    <td className={`${subTdBase} text-right tabular-nums ${
                      l.pesoNeto == null ? mutedTxt : txtNormal
                    }`}>
                      {fmtPeso(l.pesoNeto)}
                    </td>
                    {/* El bruto por debajo del neto es imposible y pasa: el peso
                        por caja está mal en el maestro. Se pinta el dato tal cual
                        —corregirlo sería inventarlo— y se señala aquí, en la fila
                        que lo causa, además de en el aviso de la carga. */}
                    <td className={`${subTdBase} text-right tabular-nums ${
                      l.pesoBruto == null
                        ? mutedTxt
                        : l.pesoNeto != null && l.pesoBruto < l.pesoNeto
                          ? txtAviso
                          : txtNormal
                    }`}>
                      {fmtPeso(l.pesoBruto)}
                    </td>
                    <td className={subTd}>
                      <PickingBar
                        pct={pct}
                        dark={dark}
                        compact
                        title={t('salesOrders.picking.lineaTooltip', {
                          picking: fmtQty(l.qtyPicking),
                          solicitada: fmtQty(l.qtySolicitada),
                          pendiente: fmtQty(l.qtyPendiente),
                        })}
                      />
                    </td>
                  </FilaMarcable>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </li>
  )
}

// ── Acción: generar documentación de la carga ────────────────────────────────

/** Abre el cajón de documentos de esta carga.
 *
 *  Va al extremo derecho de la fila, después de la barra de picking: es la
 *  posición de acción, la misma que ocupan editar y cancelar en la tabla maestra.
 *
 *  Para las empresas cuyo packing list aún no sabemos generar el botón sale
 *  deshabilitado y con el motivo en el tooltip, no oculto. Un hueco no explica
 *  nada; un botón apagado dice «esto existe y todavía no te toca». */
function DocumentosBoton({
  dark, empresa, carga, onClick,
}: { dark: boolean; empresa: string; carga: string; onClick: () => void }) {
  const { t } = useTranslation()
  const disponible = EMPRESAS_CON_PACKING.has(empresa.toLowerCase())

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!disponible}
      aria-label={t('salesOrders.documentos.abrir', { carga })}
      title={disponible
        ? t('salesOrders.documentos.abrir', { carga })
        : t('salesOrders.documentos.noDisponibleEmpresa', { empresa: empresa.toUpperCase() })}
      className={`self-center shrink-0 p-1.5 rounded-md transition-colors ${
        disponible
          ? `cursor-pointer ${dark
              ? 'text-slate-500 hover:text-sky-300 hover:bg-white/8'
              : 'text-slate-400 hover:text-sky-600 hover:bg-slate-200/70'}`
          : `cursor-not-allowed opacity-40 ${dark ? 'text-slate-600' : 'text-slate-400'}`
      }`}
    >
      <svg
        className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      >
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <path d="M14 2v6h6M9 15h6M9 11h3" />
      </svg>
    </button>
  )
}

// ── Una carga solicitada: existe en el papeleo, no en el almacén ─────────────

/** Fila de una carga pedida. Tiene dos caras según lo lejos que haya llegado:
 *
 *  - **Solicitada**: el robot aún trabaja. Sin número —no existe— y con el hueco
 *    en borde discontinuo, que dice «esto va a ser una carga» mejor que un vacío.
 *  - **Recién creada**: el robot devolvió el número y la carga ya existe en D365.
 *    Se enseña el número real, que es el dato que el usuario está esperando,
 *    aunque su picking tarde otros ~15 min en llegar desde Fabric.
 *
 *  Ninguna de las dos lleva barra de picking: no hay nada preparado todavía, y
 *  una barra al 0% se leería como «el almacén va tarde» en vez de «esto acaba de
 *  nacer».
 *
 *  Se pinta aunque nadie la mire, porque es la explicación de por qué el
 *  disponible de una línea ha bajado sin que aparezca ninguna carga.
 */
function SolicitudFila({
  solicitud: s, dark, primera, lineaPorTransid, descripcionPorTransid, marcas, clave,
}: {
  solicitud: SolicitudCarga
  dark: boolean
  primera: boolean
  lineaPorTransid?: Map<string, number | undefined>
  descripcionPorTransid?: Map<string, string | undefined>
  /** Sin almacén de marcas no hay canalón: es la pantalla de detalle. */
  marcas?: MarcasFilas
  clave: string
}) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [abierta, setAbierta] = useState(false)
  // El número manda sobre el estado: si está, la carga existe en D365.
  const creada = !!s.carga
  // Igual que en `CargaFila`: el hook se llama siempre, con almacén inerte
  // cuando la pantalla no marca.
  const marcada = useMarcada(marcas ?? SIN_MARCAS, clave)
  const marcable = !!marcas
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const divider  = dark ? 'border-white/8' : 'border-slate-200'
  const hover    = dark ? 'hover:bg-white/[0.04]' : 'hover:bg-white/70'
  const subTd    = `px-2 py-1 text-[11px] whitespace-nowrap ${dark ? 'text-slate-300' : 'text-slate-700'}`
  const subTh    = `px-2 py-1 text-left text-[9px] font-bold uppercase tracking-wider whitespace-nowrap ${dark ? 'text-slate-500' : 'text-slate-400'}`

  const cuando = s.fechaSolicitud
    ? new Date(s.fechaSolicitud).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : null

  return (
    <li className={primera ? '' : `border-t ${divider}`}>
      <div className={`flex items-stretch transition-colors duration-150 ${
        marcada ? marcadoBg : hover
      }`}>
      {marcable && (
        // Fuera del botón de desplegar: dentro, el clic de marcar burbujearía y
        // abriría también el detalle de la solicitud.
        <span className="flex items-center pl-2">
          <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
        </span>
      )}
      <button
        type="button"
        onClick={() => setAbierta(a => !a)}
        aria-expanded={abierta}
        className={`flex-1 min-w-0 flex items-center gap-3 flex-wrap py-1.5 text-left cursor-pointer ${
          marcable ? 'pl-1 pr-3' : 'px-3'
        }`}
      >
        <svg
          className={`w-3 h-3 shrink-0 transition-transform duration-150 ${abierta ? 'rotate-90' : ''} ${mutedTxt}`}
          viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <path d="M5 11l4-4-4-4" />
        </svg>

        {creada ? (
          <span className={`font-mono text-[11px] font-semibold ${
            dark ? 'text-slate-100' : 'text-slate-800'}`}>
            {s.carga}
          </span>
        ) : (
          <span
            className={`font-mono text-[11px] font-semibold px-1.5 rounded border border-dashed ${
              dark ? 'border-white/20 text-slate-500' : 'border-slate-300 text-slate-400'
            }`}
          >
            {t('salesOrders.nuevaCarga.sinNumero')}
          </span>
        )}

        <span
          title={t(creada ? 'salesOrders.nuevaCarga.creadaNota'
                          : 'salesOrders.nuevaCarga.enCurso')}
          className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
            creada
              ? (dark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700')
              : (dark ? 'bg-amber-500/15 text-amber-300' : 'bg-amber-100 text-amber-700')
          }`}
        >
          {t(creada ? 'salesOrders.nuevaCarga.badgeCreada'
                    : 'salesOrders.nuevaCarga.badge')}
        </span>

        {/* El tipo de envío sólo se pinta cuando es el excepcional: una carga
            general no necesita anunciarse. */}
        {s.tipoEnvio === 'aereo' && (
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
            dark ? 'bg-violet-500/15 text-violet-300' : 'bg-violet-100 text-violet-700'
          }`}>
            {t('salesOrders.nuevaCarga.tipoEnvio_aereo')}
          </span>
        )}

        {s.cargaCliente && (
          <span
            title={t('salesOrders.picking.referenciaTooltip')}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
              dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-100 text-sky-700'
            }`}
          >
            {s.cargaCliente}
          </span>
        )}
        {s.referenciaCliente && (
          <span
            title={t('salesOrders.picking.referencia2Tooltip')}
            className={`text-[10px] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
              dark ? 'bg-teal-500/15 text-teal-300' : 'bg-teal-100 text-teal-700'
            }`}
          >
            {s.referenciaCliente}
          </span>
        )}
        {s.noPaletizada && <PaletizadaBadge paletizada={false} dark={dark} />}

        <span className={`text-[10px] ${mutedTxt}`}>
          {t('salesOrders.picking.articulosCount', { count: s.lineas.length })}
        </span>

        <span className={`ml-auto flex items-center gap-2.5 text-[10px] ${mutedTxt}`}>
          <span className="tabular-nums">
            {fmtQty(s.unidades)} {t('salesOrders.picking.units')}
          </span>
          <span
            className="w-[130px] text-right truncate"
            title={[s.usuario, cuando].filter(Boolean).join(' · ')}
          >
            {cuando ?? ''}
          </span>
        </span>
      </button>
      </div>

      {abierta && (
        <div className={`px-3 pb-2 pl-8 overflow-x-auto border-t ${divider}`}>
          <p className={`py-1.5 text-[10px] ${mutedTxt}`}>
            {t(creada ? 'salesOrders.nuevaCarga.creadaNota'
                      : 'salesOrders.nuevaCarga.enCurso')}
          </p>
          <table className={`w-full table-fixed border-collapse text-left ${
            marcable ? 'min-w-[544px]' : 'min-w-[520px]'
          }`}>
            <colgroup>
              {marcable && <col className="w-[24px]" />}
              <col className="w-[44px]" />
              <col className="w-[260px]" />
              <col className="w-[100px]" />
            </colgroup>
            <thead>
              <tr>
                {marcable && <CabeceraMarca />}
                <th className={subTh}>{t('salesOrders.line.lineNum')}</th>
                <th className={subTh}>{t('salesOrders.line.article')}</th>
                <th className={`${subTh} text-right`}>{t('salesOrders.picking.requested')}</th>
              </tr>
            </thead>
            <tbody>
              {s.lineas.map((l, i) => {
                // La solicitud aún no tiene nº de carga, así que su fila la
                // identifican la transacción de inventario y el artículo.
                const claveL = marcable ? claveFila(clave, l.inventtransid, l.articulo) : ''
                return (
                <FilaMarcable
                  key={`${l.inventtransid}-${i}`}
                  marcas={marcas ?? SIN_MARCAS}
                  clave={claveL}
                  dark={dark}
                  className={`border-t ${divider}`}
                >
                  {marcable && (
                    <MarcaCelda marcas={marcas} clave={claveL} dark={dark} densa />
                  )}
                  <td className={`${subTd} tabular-nums ${mutedTxt}`}>
                    {lineaPorTransid?.get(l.inventtransid) ?? '—'}
                  </td>
                  <td className={subTd}>
                    <div className="max-w-[220px]">
                      <div className="font-semibold text-[11px] truncate" title={fmtArt(l.articulo)}>
                        {fmtArt(l.articulo)}
                      </div>
                      {descripcionPorTransid?.get(l.inventtransid) && (
                        <div className={`text-[9px] truncate ${mutedTxt}`}>
                          {descripcionPorTransid.get(l.inventtransid)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className={`${subTd} text-right tabular-nums`}>{fmtQty(l.cantidad)}</td>
                </FilaMarcable>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </li>
  )
}
